import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {Box, IconButton, InputAdornment, TextField, Tooltip} from "@mui/material";
import {SIDEBAR_WIDTH} from "crqlar-react-common/dist/utils/useDimensions";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {RightSidebarSearchButton} from "../../layout/rightSidebar/RightSidebarRail";

const SEARCH_BOX_WIDTH = 380;

const normalizeTitle = (text: string) => text.toLowerCase().replace(/\s+/g, " ").trim();

export const matchesTitle = (query: string, title: string, key: string = title) => {
    const needle = normalizeTitle(query);

    return needle.length === 0 || normalizeTitle(title).includes(needle) || normalizeTitle(key).includes(needle);
};

interface SearchBoxProps {
    query: string;
    onQueryChange: (query: string) => void;
    onClose: () => void;
    placeholder: string;
}

function SearchBox({query, onQueryChange, onClose, placeholder}: SearchBoxProps) {
    const {t} = useTranslation();

    useEffect(() => () => onQueryChange(""), [onQueryChange]);

    const clearAndClose = () => {
        onQueryChange("");
        onClose();
    };

    const clearAndCloseLabel = t("Clear and close search");

    return (
        <Box
            sx={{
                position: {xs: "static", md: "absolute"},
                top: {md: 0},
                right: {md: SIDEBAR_WIDTH},
                width: {xs: "100%", md: SEARCH_BOX_WIDTH},
                minWidth: 0,
                flex: {xs: "1 1 auto", md: "0 0 auto"},
                px: {xs: 0, md: 2},
                py: {xs: 0, md: 1.25},
                boxSizing: "border-box",
                bgcolor: "background.paper",
                border: {xs: 0, md: "1px solid rgba(15, 23, 42, 0.12)"},
                borderRadius: 1,
                boxShadow: {xs: "none", md: "0 12px 28px rgba(15, 23, 42, 0.12)"},
                zIndex: {md: 40},
            }}
        >
            <TextField
                autoFocus
                fullWidth
                size="small"
                color="primary"
                sx={{
                    "& .MuiInputBase-root": {height: 36},
                    "& .MuiInputAdornment-positionEnd": {ml: 0.5},
                }}
                value={query}
                placeholder={placeholder}
                onChange={event => onQueryChange(event.target.value)}
                onKeyDown={event => {
                    if (event.key === "Escape") {
                        event.stopPropagation();
                        clearAndClose();
                    }
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start" sx={{color: "primary.main"}}>
                            <SearchOutlinedIcon fontSize="small"/>
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end" sx={{color: "primary.main"}}>
                            <Tooltip title={clearAndCloseLabel}>
                                <IconButton
                                    edge="end"
                                    size="small"
                                    color="primary"
                                    data-dashboard-control=""
                                    aria-label={clearAndCloseLabel}
                                    onClick={clearAndClose}
                                >
                                    <CloseIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );
}

interface Props {
    query: string;
    onQueryChange: (query: string) => void;
    /** What the search narrows, when it is not cards (the survey evaluation filters its questions). */
    placeholder?: string;
}

export function InsightsRailSearch({query, onQueryChange, placeholder}: Props) {
    const {t} = useTranslation();

    return (
        <RightSidebarSearchButton
            icon={<SearchOutlinedIcon/>}
            label={t("Search")}
            renderPanel={close => (
                <SearchBox
                    query={query}
                    onQueryChange={onQueryChange}
                    onClose={close}
                    placeholder={placeholder ?? t("Filter insights by title")}
                />
            )}
        />
    );
}

